/* eslint-disable max-lines-per-function */
import './CoralPdpHowItWorks.scss'
import '../../../coral-library/css/_layout.scss'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'

import { Modal, useIsWindowBiggerThan } from '@bob/library'
import { AppProviderContext } from '@websrc/App/AppContext'
import { Asset } from '@websrc/App/Components/Asset/Asset'
import { CoralCardsSlider } from '@websrc/App/Components/CoralCardsSlider/CoralCardsSlider'
import { CoralHeading } from '@websrc/App/Components/CoralHeading/CoralHeading'
import { matomo } from '@websrc/App/Tracking/Matomo'
import { CoralMasterCardVisuals } from '@websrc/DataMappers/mapCoralMasterCard'
import { mapCoralWebPdpHowItWorks } from '@websrc/DataMappers/mapSpot'
import React, { FC, useContext, useMemo, useState } from 'react'

import { CoralMasterCard } from '../../../coral-library/cards/Coral/ContentCard/CoralMasterCard'
import { CoralPdpLessonStructure } from '../CoralPdpLessonStructure/CoralPdpLessonStructure'
import { CoralPdpUnitOverview } from '../CoralPdpUnitOverview/CoralPdpUnitOverview'
import { CoralPdp5eModel } from './CoralPdp5eModel'
type CoralWebPdpHowItWorksProps = ReturnType<typeof mapCoralWebPdpHowItWorks>

const howItWorksSmVisuals: CoralMasterCardVisuals = {
  headlineSize: 'h3-sm',
  bodySize: 'body-md',
  contentContainerColor: 'coral-pale',
  imageContainerBackground: 'off-white',
  contentPadding: '3xl',
  imageOrientation: 'top',
  paddingBody: 'lg',
  radius: 4,
  badgeSize: 'label-md-medium',
  buttonSize: 'sm',
  positionButton: 'start',
  hoverAnimation: 'move-up',
  radiusBadge: 4,
  badgeAbsolutePosition: 'top-left',
  imageBadgeAvailability: true,
}
const howItWorksLgVisuals: CoralMasterCardVisuals = {
  headlineSize: 'h3-md',
  bodySize: 'body-lg',
  contentContainerColor: 'coral-pale',
  imageContainerBackground: 'off-white',
  contentPadding: '3xl',
  imageOrientation: 'top',
  paddingBody: 'lg',
  radius: 4,
  badgeSize: 'label-md-medium',
  buttonSize: 'sm',
  positionButton: 'start',
  hoverAnimation: 'move-up',
  radiusBadge: 4,
  badgeAbsolutePosition: 'top-left',
  imageBadgeAvailability: true,
}

const previewSmVisuals: CoralMasterCardVisuals = {
  subheadSize: 'label-sm-medium',
  headlineSize: 'h2-sm',
  bodySize: 'body-md',
  equalSize: false,
  contentContainerColor: 'white',
  contentPadding: '3xl',
  imageOrientation: 'top',
  subheadColor: 'grey',
  imageRatio: '3-2',
  paddingHeadline: 'lg',
  paddingBody: 'lg',
  buttonWidth: 'full',
  positionContent: 'center',
  positionButton: 'start',
  radius: 4,
}
const previewMdVisuals: CoralMasterCardVisuals = {
  subheadSize: 'label-sm-medium',
  headlineSize: 'h2-sm',
  bodySize: 'body-md',
  equalSize: true,
  contentContainerColor: 'white',
  contentPadding: '4xl',
  imageOrientation: 'left',
  subheadColor: 'grey',
  imageRatio: '3-2',
  paddingHeadline: 'lg',
  paddingBody: 'lg',
  buttonWidth: 'auto',
  positionContent: 'center',
  radius: 4,
  positionButton: 'start',
}
const previewLgVisuals: CoralMasterCardVisuals = {
  subheadSize: 'label-md-medium',
  headlineSize: 'h2-md',
  bodySize: 'body-lg',
  equalSize: true,
  contentContainerColor: 'white',
  contentPadding: '6xl',
  imageOrientation: 'left',
  subheadColor: 'grey',
  imageRatio: '3-2',
  paddingHeadline: 'lg',
  paddingBody: 'lg',
  buttonWidth: 'auto',
  buttonIcon: 'upRightArrow',
  positionContent: 'center',
  positionButton: 'start',
  radius: 4,
}

const splitContentSmVisuals: CoralMasterCardVisuals = {
  subheadSize: 'label-sm-medium',
  headlineSize: 'h2-sm',
  bodySize: 'body-md',
  equalSize: true,
  contentPadding: '4xl',
  imageOrientation: 'left',
  subheadColor: 'grey',
  imageRatio: '3-2',
  paddingHeadline: 'lg',
  paddingBody: 'lg',
  buttonWidth: 'auto',
  buttonIcon: 'upRightArrow',
  positionContent: 'center',
  positionButton: 'start',
}
const splitContentMdVisuals: CoralMasterCardVisuals = {
  subheadSize: 'label-sm-medium',
  headlineSize: 'h2-sm',
  bodySize: 'body-md',
  equalSize: true,
  contentPadding: '4xl',
  imageOrientation: 'left',
  subheadColor: 'grey',
  imageRatio: '3-2',
  paddingHeadline: 'lg',
  paddingBody: 'lg',
  buttonWidth: 'auto',
  buttonIcon: 'upRightArrow',
  positionContent: 'center',
  positionButton: 'start',
}
const splitContentLgVisuals: CoralMasterCardVisuals = {
  headlineSize: 'h3-md',
  bodySize: 'body-md',
  equalSize: true,
  contentPadding: '4xl',
  imageOrientation: 'left',
  imageRatio: '1-1',
  paddingBody: '3xl',
  paddingList: 'lg',
  positionContent: 'center',
}
export const CoralPdpHowItWorks: FC<CoralWebPdpHowItWorksProps> = ({
  asset,
  model_5e,
  cards,
  header,
  description,
  cta,
  splitContentCards,
  main_header,
  main_description,
  preview_header,
  preview_description,
  preview_card,
  unit_overview,
  lesson_structure,
}) => {
  const { matomoPageTemplate } = useContext(AppProviderContext)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const handleCTAClick = () => {
    setIsModalVisible(true)
    matomo.trackEvent(
      'engagement',
      matomoPageTemplate,
      ['coral PDP HowItWorks'],
      ['coral 5E button'],
      'button'
    )
  }
  const isTablet = useIsWindowBiggerThan(719)
  const isDesktop = useIsWindowBiggerThan(1079)
  const isWidescreen = useIsWindowBiggerThan(1439)

  const howItWorksVisuals: CoralMasterCardVisuals = useMemo(
    () => (isWidescreen ? howItWorksLgVisuals : howItWorksSmVisuals),
    [isWidescreen]
  )
  const previewVisuals: CoralMasterCardVisuals = useMemo(
    () => (isDesktop ? previewLgVisuals : isTablet ? previewMdVisuals : previewSmVisuals),
    [isDesktop, isTablet]
  )
  const splitContentVisuals: CoralMasterCardVisuals = useMemo(
    () =>
      isDesktop ? splitContentLgVisuals : isTablet ? splitContentMdVisuals : splitContentSmVisuals,
    [isDesktop, isTablet]
  )
  if (preview_card?.backgroundColor === 'coral-pale')
    previewVisuals.imageContainerBackground = 'coral-light'
  else if (preview_card?.backgroundColor === 'teal-pale')
    previewVisuals.imageContainerBackground = 'teal-light'
  else previewVisuals.imageContainerBackground = 'brown-light'

  return (
    <div className={'container-coral-spot m--top-spacing-6xl'}>
      <div className={'how-it-works'}>
        <Modal
          closeCallback={() => setIsModalVisible(false)}
          loadOnInitial={{ enabled: true, visible: isModalVisible }}
          overlayBackgroundColor={'dark'}
          position={'center'}
        >
          {model_5e && <CoralPdp5eModel model_5e={model_5e} />}
        </Modal>
        <CoralHeading
          animate={false}
          description={main_description}
          header={main_header}
          headerTag={'h2'}
          headerVisual={'h1-md'}
        />
        <div className={'how-it-works__top-section-wrapper'}>
          <div className={'how-it-works__swiper-cards'}>
            <CoralCardsSlider forceRow={true}>
              {cards.map(
                (card, index) => (
                  (card.badges[0].color = 'coral-pale'),
                  (
                    <CoralMasterCard
                      key={index}
                      onCTAClick={handleCTAClick}
                      {...card}
                      visuals={howItWorksVisuals}
                    />
                  )
                )
              )}
            </CoralCardsSlider>
          </div>
          <Asset
            className={'how-it-works__main-asset'}
            lazyLoading={true}
            objectFit={'cover'}
            srcSetWidths={[400, 600, 800, 1200, 1600]}
            thumbnail={asset?.thumbnail}
            video={asset?.video}
          />
        </div>
        {unit_overview && (
          <CoralPdpUnitOverview
            cards={unit_overview.cards}
            description={unit_overview.description}
            header={unit_overview.header}
          />
        )}

        {lesson_structure && (
          <CoralPdpLessonStructure
            cards={lesson_structure.cards}
            description={lesson_structure.description}
            header={lesson_structure.header}
          />
        )}
        <div>
          <CoralHeading
            animate={false}
            description={preview_description}
            header={preview_header}
            headerTag={'h3'}
            headerVisual={'h2'}
          />
          {preview_card && (
            <div className={'how-it-works__preview-card-container'}>
              <div
                className={`how-it-works__preview-card m--background-color-${preview_card?.backgroundColor}`}
              >
                {preview_card && <CoralMasterCard {...preview_card} visuals={previewVisuals} />}
              </div>
            </div>
          )}
        </div>
        <div className={'how-it-works__bottom-section-wrapper'}>
          <CoralHeading
            animate={false}
            cta={cta}
            description={description}
            header={header}
            headerTag={'h3'}
            headerVisual={'h2-md'}
          />
          <div className={'how-it-works__split-contents'}>
            {splitContentCards.map((card, index) => {
              const visuals = { ...splitContentVisuals }
              if (isTablet) {
                if (index % 2 === 1) visuals.imageOrientation = 'right'
                else visuals.imageOrientation = 'left'
              } else {
                visuals.imageOrientation = 'top'
              }
              return <CoralMasterCard key={index} {...card} visuals={visuals} />
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
